import React, { useState } from 'react';

import { Button, Link, MarkdownRenderer } from '@ergeon/core-components';
import { ArrowTopRightOnSquareIcon, ArrowUturnLeftIcon, EyeIcon } from '@ergeon/icons/hero/micro';

import { API_HOST } from '../../../../../envs';
import { ModalController } from '../../../../../modals';
import EmailPreviewModal from '../../../../../modals/EmailPreviewModal';
import { cleanEmailBody } from '../../../../../modals/EmailPreviewModal/utils/emailFormatters';
import { formatReplySubject } from '../../utils/formatReplySubject';
import {
  autoLinkMarkdownUrls,
  closeMarkdownPairs,
  ensureParagraphBreaks,
  MARKDOWN_ELEMENT_CLASSES,
  MARKDOWN_WRAPPER_CLASSES,
} from '../../utils/markdownDisplay';
import { formatTextWithLinks } from '../../utils/textFormatters';

interface EmailMessageProps {
  subject?: string | null;
  fromEmail?: string | null;
  toEmail?: string | null;
  text?: string | null;
  textFullLength?: number | null;
  entryId?: number | null;
  emlFileUrl?: string | null;
  onReply?: (subject: string) => void;
  direction?: 'inbound' | 'outbound';
}

const MAX_TEXT_LENGTH = 300;

export const EmailMessage: React.FC<EmailMessageProps> = ({
  subject,
  fromEmail,
  toEmail,
  text,
  textFullLength,
  entryId,
  emlFileUrl,
  onReply,
  direction,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // For outbound emails (sent from app), text is clean markdown from htmlToMarkdown — skip cleaning.
  // For inbound emails, clean HTML artifacts, thread history, and noise from the raw body.
  const isOutbound = direction === 'outbound';
  const cleanedText = isOutbound ? (text || '') : cleanEmailBody(text);

  const originalTextLength = text ? text.length : 0;
  const textLength = cleanedText ? cleanedText.length : 0;
  const fullLength = textFullLength || textLength;

  // Check if we should show Read More/Less (text we have is > 300 chars)
  const shouldShowReadMore = textLength > MAX_TEXT_LENGTH;

  // Check if text from API is truncated (API didn't return full text)
  // Only show this message if we're already showing Read More (text > 300)
  const isApiTruncated = shouldShowReadMore && fullLength > originalTextLength;

  let displayText = cleanedText;
  if (shouldShowReadMore && !isExpanded) {
    displayText = closeMarkdownPairs(`${cleanedText!.substring(0, MAX_TEXT_LENGTH)}...`);
  }

  // Convert text with URLs to clickable links (for inbound emails)
  const formattedContent = displayText ? formatTextWithLinks(displayText) : null;

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex flex-col gap-1">
        <strong>{subject || 'No Subject'}</strong>
        <div className="flex flex-col gap-1 opacity-75 paragraph-2">
          <span className="opacity-75 paragraph-2">From: {fromEmail || 'Unknown'}</span>
          <span className="opacity-75 paragraph-2">To: {toEmail || 'Unknown'}</span>
        </div>
      </div>
      {displayText && (
        <div className="flex flex-col gap-1 break-words text-wrap">
          {isOutbound ? (
            <div className={MARKDOWN_WRAPPER_CLASSES}>
              <MarkdownRenderer content={ensureParagraphBreaks(autoLinkMarkdownUrls(displayText))} elementClassNames={MARKDOWN_ELEMENT_CLASSES} />
            </div>
          ) : (
            <span className="whitespace-pre-wrap">{formattedContent}</span>
          )}
          {shouldShowReadMore && (
            <Link
              onClick={(e) => {
                e.preventDefault();
                setIsExpanded(!isExpanded);
              }}>
              {isExpanded ? '↑ Read Less' : '↓ Read More'}
            </Link>
          )}
        </div>
      )}
      {isApiTruncated && (
        <div className="opacity-50 paragraph-3">(Truncated, full length: {fullLength} characters)</div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {emlFileUrl ? (
          <Button
            flavor="regular"
            onClick={() => ModalController.show(EmailPreviewModal, { emlUrl: emlFileUrl, subject: subject || undefined })}
            size="small"
            taste="line">
            <EyeIcon className="size-4" />
            Preview Email
          </Button>
        ) : (
          <span className="text-gray-500 dark:text-gray-400 italic paragraph-3">Preview will be ready soon</span>
        )}
        {entryId && (
          <Button
            asAnchor
            flavor="regular"
            href={`${API_HOST}/public-admin/communications/email/${entryId}/change/`}
            size="small"
            target="_blank"
            taste="line">
            <ArrowTopRightOnSquareIcon className="size-4" />
            View in Admin
          </Button>
        )}
        {onReply && (
          <Button
            flavor="regular"
            onClick={() => onReply(formatReplySubject(subject))}
            size="small"
            taste="line">
            <ArrowUturnLeftIcon className="size-4" />
            Reply
          </Button>
        )}
      </div>
    </div>
  );
};
